import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

import { formatDate, formatDateTime } from "@/lib/datetime";
export default function DeliveryChallanPrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/sales/stock-transfers/${id}/challan`);
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => { if (!loading && data) setTimeout(() => window.print(), 400); }, [loading, data]);
  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!data) return <div className="p-8 text-red-600">Transfer not found</div>;

  const tr = data.transfer;
  const biz = data.business || {};
  const src = tr.source || {};
  const dst = tr.dest || {};
  const totalQty = tr.lines.reduce((s, l) => s + Number(l.qty || 0), 0);

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print { .no-print { display: none !important; } body { background: white; } }
      `}</style>
      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 p-3 flex items-center gap-3 shadow-sm">
        <button onClick={() => window.history.back()} className="h-9 w-9 rounded-md border border-gray-200 hover:bg-gray-50 flex items-center justify-center"><ArrowLeft className="h-4 w-4" /></button>
        <span className="font-heading font-semibold text-gray-900">Delivery Challan Preview — {tr.transfer_no}</span>
        <button onClick={() => window.print()} className="ml-auto h-9 px-3 rounded-md bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white text-sm font-semibold flex items-center gap-1.5" data-testid="dc-print-do"><Printer className="h-4 w-4" /> Print</button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white shadow-sm print:shadow-none p-8 my-6 print:my-0 print:p-0 text-gray-900">
        <div className="flex items-start justify-between border-b-2 border-[#9B1C1C] pb-4 mb-6">
          <div>
            {biz.logo_url && <img src={biz.logo_url} alt="Logo" className="h-14 mb-2 object-contain" />}
            <div className="font-heading font-bold text-2xl text-[#9B1C1C]">{biz.business_name || "Thara Gold"}</div>
            <div className="text-xs text-gray-700 mt-1 leading-tight">
              {[biz.address_line1, biz.address_line2].filter(Boolean).join(", ")}<br />
              {[biz.city, biz.state, biz.pincode].filter(Boolean).join(", ")}<br />
              {biz.phone && <>Ph: {biz.phone}</>}
              {biz.gstin && <> · GSTIN {biz.gstin}</>}
            </div>
          </div>
          <div className="text-right">
            <div className="uppercase tracking-widest text-[11px] text-gray-500">Delivery Challan</div>
            <div className="text-[9px] text-gray-400 mt-0.5">(Not a Tax Invoice — Stock movement within same GSTIN)</div>
            <div className="font-heading font-bold text-xl text-gray-900 mt-1">{tr.transfer_no}</div>
            <div className="text-xs text-gray-600 mt-1">Date: <strong>{formatDate(tr.transfer_date)}</strong></div>
            <div className={`inline-block mt-1 text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${tr.status === "received" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : tr.status === "approved" ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-red-50 border-red-200 text-red-800"}`}>{tr.status}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">From (Consignor)</div>
            <div className="font-semibold text-sm">{src.name}</div>
            <div className="text-xs text-gray-700 leading-tight">{src.address || "—"}</div>
            {src.gstin && <div className="text-[11px] font-mono text-gray-700 mt-1">GSTIN: <strong>{src.gstin}</strong></div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">To (Consignee)</div>
            <div className="font-semibold text-sm">{dst.name}</div>
            <div className="text-xs text-gray-700 leading-tight">{dst.address || "—"}</div>
            {dst.gstin && <div className="text-[11px] font-mono text-gray-700 mt-1">GSTIN: <strong>{dst.gstin}</strong></div>}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-4 text-xs">
          <Detail label="Transporter" value={tr.transporter} />
          <Detail label="Vehicle #" value={tr.vehicle_no} mono />
          <Detail label="LR #" value={tr.lr_no} mono />
          <Detail label="E-way Bill #" value={tr.eway_bill_no} mono />
        </div>

        <table className="w-full text-xs mb-4 border border-gray-300">
          <thead className="bg-gray-100">
            <tr className="text-left border-b border-gray-300">
              <th className="px-2 py-2 w-6 text-center">#</th>
              <th className="px-2 py-2">Description of Goods</th>
              <th className="px-2 py-2 w-24 text-center">HSN</th>
              <th className="px-2 py-2 w-24 text-right">Quantity</th>
            </tr>
          </thead>
          <tbody>
            {tr.lines.map((ln, i) => (
              <tr key={i} className="border-b border-gray-200">
                <td className="px-2 py-1.5 text-center text-gray-500">{i + 1}</td>
                <td className="px-2 py-1.5">
                  <div className="font-medium">{ln.product_name}</div>
                  {ln.product_sku && <div className="text-[10px] text-gray-500 font-mono">{ln.product_sku}</div>}
                </td>
                <td className="px-2 py-1.5 text-center font-mono">{ln.hsn_code || "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{ln.qty}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 border-t-2 border-[#9B1C1C]">
              <td colSpan={3} className="px-2 py-2 text-right font-semibold">Total Quantity</td>
              <td className="px-2 py-2 text-right tabular-nums font-heading font-bold text-[#9B1C1C]">{totalQty}</td>
            </tr>
          </tbody>
        </table>

        {tr.notes && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Notes</div>
            <div className="text-xs whitespace-pre-line">{tr.notes}</div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-3 gap-6 text-xs">
          <div>
            <div className="border-t border-gray-400 pt-1 mt-16 font-semibold">Prepared By</div>
          </div>
          <div className="text-center">
            <div className="border-t border-gray-400 pt-1 mt-16 font-semibold">Received By (Signature & Stamp)</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">For {biz.business_name || "Thara Gold"}</div>
            {biz.invoice_signature_url && <img src={biz.invoice_signature_url} alt="Signature" className="h-12 ml-auto my-2" />}
            <div className="border-t border-gray-400 mt-10 pt-1 font-semibold">Authorised Signatory</div>
          </div>
        </div>

        <div className="mt-4 text-[10px] text-gray-500 italic border-t border-gray-200 pt-2 text-center">
          This is a Delivery Challan issued for stock movement within the same GSTIN. No sale is intended and no GST is levied.
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`${mono ? "font-mono" : ""} text-gray-800`}>{value || "—"}</div>
    </div>
  );
}
